import type React from 'react';
import { useTranslation } from 'react-i18next';
import { ComboBox, EmptyState, Input, Label, ListBox } from '@heroui/react';
import type { NameOption } from '@/pages/catalogImport/names';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';

/** The row's searchable pickers: the product's category and brand, the batch's supplier. */
export type RowDrawerPickerField = 'category' | 'brand' | 'supplier';

type RowDrawerPickerProps = {
    field: RowDrawerPickerField;
    label: string;
    value: string | null;
    /** The catalog's entities, then the file's new names (`isFileText`). */
    options: NameOption[];
    isDisabled: boolean;
    onChange: (key: string | null) => void;
};

/**
 * A labelled HeroUI `ComboBox` over one flat list: the catalog's entities by
 * name (categories by path), then the file's names the import would create,
 * each shown as "New: <text>", so a row can move to another row's new name.
 * The contains-filter searches what each option shows.
 *
 * One flat dynamic list (`defaultItems` and a render function), as the Names
 * step's picker: grouped into sections, React's development-build render
 * logging threw on React Aria's collection nodes when filtering.
 *
 * `getNodeTestIdProp` (data-testid only, no `id`): an explicit `id` on the
 * RAC ComboBox root triggers a setState loop.
 */
export const RowDrawerPicker: React.FC<RowDrawerPickerProps> = ({
    field,
    label,
    value,
    options,
    isDisabled,
    onChange,
}) => {
    const { t } = useTranslation('pages');
    const textOf = (option: NameOption): string =>
        option.isFileText
            ? t('catalogImport.products.drawer.newOption', {
                  text: option.label,
              })
            : option.label;

    return (
        <ComboBox<NameOption>
            className="w-full"
            defaultItems={options}
            isDisabled={isDisabled}
            menuTrigger="focus"
            onSelectionChange={(key) =>
                onChange(key === null ? null : String(key))
            }
            selectedKey={value}
            {...getNodeTestIdProp(`catalog-import-row-drawer-${field}`)}
        >
            <Label>{label}</Label>
            <ComboBox.InputGroup>
                <Input
                    placeholder={t(
                        'catalogImport.products.drawer.placeholders.search',
                    )}
                />
                <ComboBox.Trigger />
            </ComboBox.InputGroup>
            <ComboBox.Popover>
                <ListBox<NameOption>
                    renderEmptyState={() => (
                        <EmptyState>
                            {t('catalogImport.products.drawer.noMatches')}
                        </EmptyState>
                    )}
                >
                    {(option) => (
                        <ListBox.Item id={option.id} textValue={textOf(option)}>
                            {textOf(option)}
                            <ListBox.ItemIndicator />
                        </ListBox.Item>
                    )}
                </ListBox>
            </ComboBox.Popover>
        </ComboBox>
    );
};
